import { useEffect, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

const HexNut = ({ hovering }) => (
  <svg width="34" height="34" viewBox="0 0 34 34" fill="none" style={{ overflow: "visible" }}>
    {/* precision ring — default state */}
    <motion.g
      animate={{ opacity: hovering ? 0 : 1, scale: hovering ? 0.7 : 1 }}
      transition={{ duration: 0.22 }}
      style={{ transformBox: "fill-box", transformOrigin: "center" }}
    >
      <circle cx="17" cy="17" r="10" stroke="#1E56A0" strokeWidth="1.5" />
      {[0, 90, 180, 270].map((a) => (
        <line
          key={a}
          x1="17" y1="3.5" x2="17" y2="7.5"
          stroke="#1E56A0"
          strokeWidth="1.5"
          transform={`rotate(${a} 17 17)`}
        />
      ))}
      <circle cx="17" cy="17" r="1.8" fill="#1E56A0" />
    </motion.g>
    {/* hex nut — hover state */}
    <motion.path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17 5 27.4 11v12L17 29 6.6 23V11L17 5Zm0 8a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9Z"
      fill="rgba(30,86,160,0.10)"
      stroke="#1E56A0"
      strokeWidth="1.7"
      animate={{ opacity: hovering ? 1 : 0, rotate: hovering ? 90 : 0 }}
      transition={{ rotate: { type: "spring", stiffness: 200, damping: 16 }, opacity: { duration: 0.2 } }}
      style={{ transformBox: "fill-box", transformOrigin: "center" }}
    />
  </svg>
);

export default function CustomCursor() {
  const [enabled, setEnabled] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [pressed, setPressed] = useState(false);
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const sx = useSpring(x, { stiffness: 400, damping: 28 });
  const sy = useSpring(y, { stiffness: 400, damping: 28 });

  useEffect(() => {
    const fine =
      window.matchMedia("(pointer: fine)").matches && window.innerWidth >= 768;
    if (!fine) return;
    setEnabled(true);

    const move = (e) => {
      x.set(e.clientX - 17);
      y.set(e.clientY - 17);
    };
    const over = (e) => {
      setHovering(
        !!(e.target.closest && e.target.closest("a,button,input,select,textarea,label,[data-cursor]"))
      );
    };
    const down = () => setPressed(true);
    const up = () => setPressed(false);

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseover", over);
    window.addEventListener("mousedown", down);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseover", over);
      window.removeEventListener("mousedown", down);
      window.removeEventListener("mouseup", up);
    };
  }, [x, y]);

  if (!enabled) return null;

  return (
    <motion.div
      className="fixed top-0 left-0 z-[9999] pointer-events-none"
      style={{ x: sx, y: sy }}
      data-testid="custom-cursor"
    >
      <motion.div
        animate={{
          scale: pressed ? 0.8 : hovering ? 1.45 : 1,
        }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        style={{
          filter: hovering ? "drop-shadow(0 0 8px rgba(30,86,160,0.45))" : "none",
        }}
      >
        <HexNut hovering={hovering} />
      </motion.div>
    </motion.div>
  );
}
